import { api } from "@/lib/api";

/**
 * The ticket API, as the front end sees it.
 *
 * One module so every screen agrees on the shape, and so a field the server stopped
 * sending is a TypeScript error in one place rather than `undefined` rendered as blank in
 * four. `strict` is on and CLAUDE.md bans `any` in code touching ticket data, so these
 * types are the contract.
 */

/**
 * ⚠️ EXACTLY the members of the server's `TicketStatus`, and there is a test that says so.
 *
 * "Triage" used to be here and has never existed in the API. It reached the queue's status
 * filter as an option, and choosing it sent a value the endpoint could not bind — so the
 * whole request failed and the screen reported that nothing matched the filter, which was a
 * statement about the caller's own data and untrue.
 */
export type TicketStatus = "New" | "InProgress" | "AwaitingUser" | "OnHold" | "Resolved" | "Closed";

export type TicketPriority = "Low" | "Normal" | "High" | "Urgent" | "Critical";

export interface TicketSummary {
  number: string;

  /** Short sequential number for reading aloud. Never a lookup key — links use `number`. */
  displayNumber: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  teamName: string;
  categoryName: string;
  siteName: string | null;
  requesterName: string;

  /**
   * Who they are, so their face can be drawn beside the name (D-38).
   *
   * ⚠️ `hasAvatar` is what stops a page of 25 rows firing 25 image requests that 404 for
   * everybody who has never uploaded a picture. Still no email and no UPN in a list.
   */
  requesterUserId: string;
  requesterHasAvatar: boolean;

  /**
   * The requester's job title matches a VIP rule, or an administrator said so.
   *
   * ⚠️ Presentation only. VIP grants nothing and hides nothing — the server never consults
   * it in an authorization decision, and no row is shown or withheld because of it. Marking
   * a row is the entire purpose.
   */
  isRequesterVip: boolean;

  assigneeName: string | null;

  /** Null together with `assigneeName` — an unassigned row has nobody to draw. */
  assigneeUserId: string | null;
  assigneeHasAvatar: boolean;

  createdAtUtc: string;
  updatedAtUtc: string;
  isOpen: boolean;

  /**
   * Resolution deadline and whether it has been missed. Null when no policy applied.
   *
   * Breach is decided by the server rather than by comparing the due date to the browser's
   * clock — a workstation set wrong would otherwise show a different queue from the one
   * beside it.
   */
  resolutionDueAtUtc: string | null;
  isResolutionBreached: boolean;

  /** One word the queue colours by. Decided by the server, never from the browser clock. */
  slaState: SlaState;
}

export type SlaState = "None" | "OnTrack" | "AtRisk" | "Breached" | "Paused" | "Met";

/**
 * How each state reads in the queue.
 *
 * ⚠️ Colour is never the only signal — every row carries the label as text too. A
 * colour-blind reader and a high-contrast theme both lose the hue, and "which of these is
 * about to breach" is not a question to answer with a shade of amber alone.
 *
 * OnTrack and Met are deliberately unstyled. If everything is marked, nothing is: the
 * point of the colour is that the four rows needing attention stand out from the ninety
 * that do not.
 */
export const slaTone: Record<SlaState, { label: string; dot: string; text: string } | null> = {
  None: null,
  OnTrack: null,
  Met: null,
  AtRisk: { label: "Due soon", dot: "bg-warning", text: "text-warning" },
  Breached: { label: "Overdue", dot: "bg-destructive", text: "text-destructive" },
  Paused: { label: "Waiting", dot: "bg-muted-foreground/60", text: "text-muted-foreground" },
};

export interface TicketPage {
  items: TicketSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface TicketDetail {
  number: string;
  displayNumber: number;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  teamName: string;
  categoryName: string;
  sensitivity: string;

  /**
   * The site this request was raised about — the only site on the response (D-82).
   *
   * A second field carried where the requester's own account says they work. D-43 derives
   * this one from that placement whenever they have one, so the two agreed on every request
   * raised since, and the server no longer sends it.
   */
  siteName: string | null;

  /**
   * The same site as an id, so the correction dialog opens on the one already recorded.
   *
   * ⚠️ Reference data, not a disclosure: `/api/reference` lists every open site to anybody
   * signed in. Null only on a request raised before a site was required (D-100).
   */
  siteId: string | null;

  requesterName: string;

  /**
   * The requester's user id, for anyone who can read the ticket (D-38, amending D-32).
   *
   * ⚠️ This used to be null unless you held `user.manage`, and the two jobs it was doing
   * have been separated. It is an identifier, needed to draw a face. Whether the name is a
   * LINK to that person's record is `canViewPerson` below — still `user.manage`, still
   * re-checked by the People endpoint.
   */
  requesterUserId: string;
  requesterHasAvatar: boolean;

  /**
   * The number to ring about this request, in E.164, and the extension to ask for.
   *
   * ⚠️ From the TICKET, not from the requester's profile as it stands today. A request is
   * raised from wherever somebody happened to be and answered days later — following the
   * profile would rewrite the callback number on an old ticket the moment they edited it.
   *
   * Null on any request raised before this was collected. The panel omits the row rather
   * than drawing an empty one.
   */
  contactPhone: string | null;
  contactPhoneExtension: string | null;

  /**
   * Whether to render the requester's name as a link to their record.
   *
   * ⚠️ Rendering only, like every other `can*` here. `/people/$id` and the endpoint behind
   * it both require `user.manage`, so flipping this in devtools produces a link that 404s.
   */
  canViewPerson: boolean;

  /**
   * Who actually filed it, when that is not the requester.
   *
   * ⚠️ Null on an ordinary ticket, and it has to be SHOWN when it is not. A request
   * raised on somebody's behalf otherwise reads as though they wrote it — a record
   * saying something untrue about a named person.
   */
  submittedByName: string | null;

  assigneeName: string | null;
  assigneeUserId: string | null;
  assigneeHasAvatar: boolean;

  createdAtUtc: string;
  updatedAtUtc: string;
  resolvedAtUtc: string | null;
  closedAtUtc: string | null;
  onHoldUntilUtc: string | null;
  onHoldReason: string | null;
  isOpen: boolean;
  allowedTransitions: TicketStatus[];

  /**
   * What this caller may do, decided by the server.
   *
   * ⚠️ For RENDERING only. Every action re-checks on its own endpoint against the
   * specific record, so editing these in devtools yields buttons that return 404.
   */
  canComment: boolean;
  canCommentInternal: boolean;
  canAssign: boolean;

  /**
   * There is at least one entry in {@link allowedTransitions}.
   *
   * ⚠️ Derived from that list server-side, not from a permission of its own. It used to be
   * `ticket.update`, which no requester holds — so the moves a requester is entitled to
   * (reopen, D-23; close, D-61) were filtered into the list correctly and then hidden, because
   * this flag turns the whole panel off.
   */
  canChangeStatus: boolean;

  canChangePriority: boolean;

  /**
   * The caller may correct who this request is for — `ticket.requester.change`.
   *
   * ⚠️ Its own flag and its own permission, deliberately not `ticket.update`. Correcting a
   * requester hands a named colleague sight of a PHI-bearing request and takes it away from
   * somebody else; editing a field on one does not. The permission is seeded onto Owner and
   * Super Admin only, so this is false for an ordinary agent until an Owner grants it.
   *
   * Rendering only, as ever — the endpoint re-checks against the record, which is where the
   * permission's Team and Category scopes actually apply.
   */
  canChangeRequester: boolean;

  /**
   * The caller may correct which site this request came from (D-100) — `ticket.update`.
   *
   * ⚠️ A correction, not a disclosure: site is descriptive metadata and never an access
   * boundary (D-14), so this moves nobody's sight of the request. It changes which queue it is
   * found in and which figures it lands in, which is why it is audited and why a requester
   * does not hold it.
   *
   * Rendering only — the endpoint re-checks against the record and answers 404 when the
   * caller cannot reach it.
   */
  canChangeSite: boolean;

  /**
   * The caller may hand this request to another team (D-96).
   *
   * ⚠️ Its own flag, computed server-side from `ticket.route`. Rendering only — the endpoint
   * re-checks against this record and answers 404 when the caller may not reach it.
   */
  canRoute: boolean;

  /**
   * The caller may run a work timer on this request — `ticket.update`.
   *
   * ⚠️ Its own flag, and no longer the same answer as {@link canChangeStatus}: that one is now
   * derived from the available moves, while this stays on `ticket.update`. Recording somebody's
   * working time is a different act from changing a status.
   */
  canRecordWork: boolean;

  /** The caller is the requester and the ticket is closed, so they may rate it (D-24). */
  canRate: boolean;

  /**
   * The caller may use canned responses — an agent writing aid. A requester replying on
   * their own request is not offered agent phrasing, and the endpoint behind the picker
   * refuses regardless of what this says.
   */
  canUseCannedResponses: boolean;

  /**
   * How this request has been chased, and whether this caller may chase it (D-49).
   *
   * Null when the feature is switched off, so the panel is absent rather than empty.
   */
  escalation: TicketEscalationState | null;

  /**
   * The commitment on this request.
   *
   * All null when no policy existed when it was raised — a ticket with no deadline is not
   * a breached one, and showing "overdue" for something nobody promised would be worse
   * than showing nothing.
   */
  /**
   * ⚠️ The first-reply fields are null for anyone who does not work tickets — the server
   * withholds them, this is not a UI decision (D-38).
   *
   * The resolution target is a promise made to the requester and they see it, met or
   * missed. The first-reply target is an internal measure of how fast the desk picks work
   * up; a red "First reply — overdue" on somebody's own request announces they were let
   * down and gives them nothing to do about it.
   *
   * Do not reintroduce a client-side check here. If these arrive null, they were not sent.
   */
  firstResponseDueAtUtc: string | null;
  resolutionDueAtUtc: string | null;
  firstRespondedAtUtc: string | null;
  isFirstResponseBreached: boolean;
  isResolutionBreached: boolean;

  /** The clock is stopped because the desk is waiting on somebody else. */
  slaPaused: boolean;

  concurrencyToken: string;
}

export type CommentKind = "Reply" | "InternalNote" | "SystemEvent";

export type CommentAuthorKind = "Requester" | "Agent" | "System";

export interface TicketComment {
  id: string;
  kind: CommentKind;
  authorName: string;

  /**
   * The author, so their face can be drawn beside their reply (D-38).
   *
   * ⚠️ An id beside a name already on screen is not enumeration: internal notes are still
   * excluded server-side before serialisation, so an author whose entry is withheld is not
   * named here either, and no endpoint turns a user id into a person without `user.manage`
   * re-checking.
   */
  authorUserId: string;
  authorHasAvatar: boolean;

  /**
   * Who the entry is from, in role terms, decided server-side.
   *
   * Not derived here by comparing `authorName` to the ticket's requester: two colleagues
   * sharing a display name is ordinary, and misattributing a reply on a request about
   * access to a patient record is not a cosmetic bug.
   */
  authorKind: CommentAuthorKind;
  body: string;
  createdAtUtc: string;
  editedAtUtc: string | null;
}

/**
 * How a request has been chased (D-49).
 *
 * ⚠️ Visible to everybody who can read the ticket, including the requester. Escalation is
 * a fact ABOUT the request rather than private correspondence, and hiding it produces the
 * failure it exists to prevent: somebody presses again because nothing said the first press
 * landed, and the desk gets two identical alerts.
 */
export interface TicketEscalationState {
  count: number;
  lastRaisedAtUtc: string | null;
  lastRaisedByName: string | null;

  /** Advisory, like every other `can` here. The endpoint re-checks all of it. */
  canEscalate: boolean;

  /** When this caller's cooldown ends, so the button can say *when*. */
  cooldownEndsAtUtc: string | null;

  raisedByMe: boolean;
}

/**
 * Asks for a request to be looked at sooner.
 *
 * ⚠️ Changes nothing on the ticket — not priority, status, assignee or SLA. It records the
 * ask and notifies whoever can act on it. Returns the refreshed detail so the panel shows
 * the new count without a second round trip.
 */
export function escalateTicket(number: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/escalate`, {
    method: "POST",
  });
}

export function getTicket(number: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}`);
}

/**
 * The thread.
 *
 * Internal notes the caller may not see are excluded server-side, before serialisation —
 * they are not in the response to be filtered. The mock sent everything and hid notes in
 * React, which is defect D2.
 */
export function getComments(number: string): Promise<TicketComment[]> {
  return api<TicketComment[]>(`/api/tickets/${encodeURIComponent(number)}/comments`);
}

export function addComment(
  number: string,
  body: string,
  isInternal: boolean,
): Promise<TicketComment> {
  return api<TicketComment>(`/api/tickets/${encodeURIComponent(number)}/comments`, {
    method: "POST",
    body: { body, isInternal },
  });
}

export function changeStatus(number: string, status: TicketStatus): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/status`, {
    method: "POST",
    body: { status },
  });
}

export function changePriority(number: string, priority: TicketPriority): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/priority`, {
    method: "POST",
    body: { priority },
  });
}

/**
 * Points a request at the person it is actually for.
 *
 * ⚠️ A disclosure, not a field edit: the named person gains sight of the request and its
 * history, and the previous requester loses both. The server audits it at High severity,
 * records it on the thread and tells the new requester.
 *
 * ⚠️ An id, never an email — email is display metadata in this system and never a lookup key
 * (D3). The id comes from the requester picker.
 *
 * Who SUBMITTED the request is not touched by this and cannot be: that is an observation the
 * server recorded, and there is no field for it on the way in.
 */
export function changeRequester(number: string, requesterUserId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/requester`, {
    method: "POST",
    body: { requesterUserId },
  });
}

/**
 * Corrects where a request came from.
 *
 * ⚠️ There is no way to spell "no site". A request must have one (D-100), so the command
 * carries a required id and the server refuses an unknown or closed site rather than storing
 * it — there is no foreign key behind this column, so an id that resolves to nothing would
 * show as a blank site for ever.
 *
 * ⚠️ It does not move the deadline. Site picks the working calendar for a team keeping no
 * hours of its own, and re-running the clock on a correction would change what somebody was
 * promised as a side effect of fixing a label.
 */
export function changeSite(number: string, siteId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/site`, {
    method: "POST",
    body: { siteId },
  });
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export interface TicketAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: "Pending" | "Clean" | "Infected" | "Failed";
  /**
   * False until a malware scan has passed. The file is still listed — hiding it would
   * leave someone re-uploading something they can see is already there.
   */
  isDownloadable: boolean;
  uploadedByName: string;
  uploadedAtUtc: string;
}

/**
 * The list, plus whether this caller can add to it.
 *
 * `canUpload` comes from the server for the same reason every other permission does:
 * deciding it in the browser is how the mock leaked privilege into the front end (D1).
 * It governs rendering only — the upload endpoint re-checks the feature flag, the
 * permission and the storage configuration itself.
 */
export interface AttachmentList {
  items: TicketAttachment[];
  canUpload: boolean;
  /** Set only when there is something worth telling the user, e.g. the feature is off. */
  unavailableReason: string | null;
}

/**
 * The per-file cap, mirrored so the browser can refuse an obviously oversized file
 * without spending a round trip on it.
 *
 * ⚠️ A mirror, not the rule. The server enforces this — and enforces it on the bytes it
 * actually received, before the form is even parsed. Nothing here is load-bearing.
 */
export const MaxAttachmentBytes = 5 * 1024 * 1024;

/**
 * The per-ticket total, mirrored for the same reason and with the same caveat.
 *
 * Used on the new-request form, where several files are chosen before any of them is
 * sent — so without it the person learns their fourth file put them over the limit only
 * after the request has already been created.
 */
export const MaxTicketAttachmentBytes = 50 * 1024 * 1024;

/**
 * What the file picker offers.
 *
 * A hint, not a control: the server decides from the file's own bytes, so a renamed
 * executable is refused whatever this says and whatever the picker allowed. It is here
 * so the common case does not involve choosing a file and then being told no.
 */
export const AttachmentAccept =
  ".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.log,.docx,.xlsx,.pptx,.zip";

export function getAttachments(number: string): Promise<AttachmentList> {
  return api<AttachmentList>(`/api/tickets/${encodeURIComponent(number)}/attachments`);
}

/**
 * Uploads one file.
 *
 * Sent as multipart rather than base64 JSON so the browser streams it. The server
 * detects the type from the file's own bytes and ignores both the extension and the
 * Content-Type this request declares.
 */
export async function uploadAttachment(number: string, file: File): Promise<TicketAttachment> {
  const form = new FormData();
  form.append("file", file);

  return api<TicketAttachment>(`/api/tickets/${encodeURIComponent(number)}/attachments`, {
    method: "POST",
    // FormData sets its own multipart boundary; the api helper leaves the body alone
    // and still attaches the antiforgery header.
    rawBody: form,
  });
}

/**
 * The download URL.
 *
 * A plain link, not a fetch. The server checks the ticket, checks the scan, audits the
 * read, then redirects to a short-lived single-file SAS on a different hostname — so the
 * bytes never come from this origin and cannot execute against this session.
 */
export function attachmentDownloadUrl(number: string, attachmentId: string): string {
  return `/api/tickets/${encodeURIComponent(number)}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

// ---------------------------------------------------------------------------
// Profile pictures
// ---------------------------------------------------------------------------

/** 2 MB. Mirrored for a fast refusal; the server enforces it on the bytes received. */
export const MaxAvatarBytes = 2 * 1024 * 1024;

/**
 * Raster formats only — and note what is absent. SVG is XML that executes script when
 * rendered, and an avatar is the one uploaded file this app renders. The server refuses
 * it from the file's own bytes; this only keeps the picker from offering it.
 */
export const AvatarAccept = ".png,.jpg,.jpeg,.gif,.webp";

export interface AvatarState {
  /** Stored AND cleared by a scan — i.e. actually visible. */
  hasPicture: boolean;

  /**
   * A picture exists and is still being checked.
   *
   * ⚠️ Distinct from `!hasPicture && scanStatus === "Pending"`, which is also what somebody
   * with no picture at all looks like — Pending is the enum's zero, so an empty row reports
   * it. Treating the two as the same polls for ever on behalf of someone not waiting.
   */
  isAwaitingScan: boolean;

  scanStatus: "Pending" | "Clean" | "Infected" | "Failed";

  /** Set when there is something worth saying, e.g. that a scan is still running. */
  message: string | null;
}

/**
 * The state of your OWN picture.
 *
 * The image endpoint cannot answer this: it 404s for "none", "not scanned" and "failed"
 * identically, so that rendering somebody else's face leaks nothing. That is useless to
 * the person waiting to see their own, which is what this is for.
 */
export function getMyAvatarState(): Promise<AvatarState> {
  return api<AvatarState>("/api/me/avatar");
}

export async function uploadAvatar(file: File): Promise<AvatarState> {
  const form = new FormData();
  form.append("file", file);

  return api<AvatarState>("/api/me/avatar", { method: "POST", rawBody: form });
}

export function removeMyAvatar(): Promise<void> {
  return api<void>("/api/me/avatar", { method: "DELETE" });
}

/** Administrator moderation. Removal only — nobody sets a picture on another account. */
export function removeUserAvatar(userId: string): Promise<void> {
  return api<void>(`/api/users/${encodeURIComponent(userId)}/avatar`, { method: "DELETE" });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/** Unread updates sitting on one request. */
export interface UnreadTicket {
  /** Opaque. What the link is built on. */
  number: string;
  /** Readable. What the badge is drawn beside (D-35). */
  displayNumber: number;
  count: number;
}

export interface Dashboard {
  openCount: number;
  unassignedCount: number;
  assignedToMeCount: number;
  urgentCount: number;
  resolvedThisWeekCount: number;
  awaitingYouCount: number;

  /**
   * Unread in-app notifications — the same rows, and therefore the same number, the bell
   * counts. Zero and empty when in-app notifications are switched off.
   */
  unreadCount: number;
  unreadTicketCount: number;
  unread: UnreadTicket[];

  /** Null when rating requests are switched off. */
  awaitingYourRatingCount: number | null;

  /**
   * The three service-desk counts.
   *
   * ⚠️ Null for anybody who does not work requests, and null because THE SERVER DID NOT SEND
   * THEM — not because this file chose to ignore them. A figure hidden in React is a figure
   * still on the wire, which is the mock's D2 defect in miniature. Nothing here should ever
   * default one of these to 0: the absence is the answer.
   */
  awaitingDeskReplyCount: number | null;
  awaitingYourReplyCount: number | null;
  goneQuietCount: number | null;

  recent: TicketSummary[];
}

/**
 * Counts, scoped by the same filter and RLS that scope the rows.
 *
 * A count is a disclosure — "there are 47 tickets you cannot see" answers a question the
 * caller was not entitled to ask — so these are never computed outside the caller's view.
 */
export function getDashboard(): Promise<Dashboard> {
  return api<Dashboard>("/api/dashboard");
}

/**
 * How many unread updates the dashboard reported for one request.
 *
 * ⚠️ Keyed on the OPAQUE number (D-4). The readable one is what the badge sits beside and is
 * never what anything looks up by.
 *
 * Returns 0 for a request the server did not name — the list is capped, and in-app
 * notifications may be switched off entirely. Absence is "nothing to show", never "unknown".
 */
export function unreadFor(data: Dashboard | null, number: string): number {
  return data?.unread.find((u) => u.number === number)?.count ?? 0;
}

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

export interface ReferenceCategory {
  id: string;
  name: string;
  sensitivity: string;
}

export interface ReferenceTeam {
  id: string;
  name: string;
  description: string;
  categories: ReferenceCategory[];
}

export interface ReferenceSite {
  id: string;
  code: string;
  name: string;
  city: string;
}

export interface Reference {
  teams: ReferenceTeam[];
  sites: ReferenceSite[];
}

export function getReference(): Promise<Reference> {
  return api<Reference>("/api/reference");
}

/**
 * Creates a ticket.
 *
 * The idempotency key is generated per attempt and reused across retries, so a flaky
 * connection cannot produce two tickets from one submission. The server returns the
 * original rather than creating a duplicate — which matters because the user has no way
 * to tell whether their first attempt landed.
 *
 * Note what is NOT sent: no team, no status, no requester, no priority. The server
 * derives every one of those, and the command type has no field to put them in.
 */
export function createTicket(input: {
  categoryId: string;
  subject: string;
  description: string;

  /**
   * Where the request is coming from.
   *
   * ⚠️ The server REQUIRES a site and refuses a create without one (D-100), but it takes the
   * requester's own placement first and reads this only when they have none — which is why
   * it is still optional here. Sending it for somebody already placed changes nothing.
   */
  siteId?: string;
  idempotencyKey: string;

  /**
   * Where the service desk should ring about this request, and the extension to ask for.
   *
   * ⚠️ Required by the server, which refuses a create without a ringable number and says
   * why. Sent as typed — the server normalises to E.164 and is the authority on the format.
   */
  contactPhone: string;
  contactPhoneExtension?: string;

  /**
   * Who the request is FOR, when that is not the person submitting it.
   *
   * ⚠️ Omit it in the ordinary case. Setting it makes the ticket belong to somebody else
   * — they see it and they are notified about it — and the server refuses unless the
   * caller may create tickets beyond their own. Who actually filed it is recorded and
   * shown on the ticket, so the record never claims they wrote it themselves.
   */
  requesterUserId?: string;
}): Promise<TicketDetail> {
  return api<TicketDetail>("/api/tickets", {
    method: "POST",
    idempotencyKey: input.idempotencyKey,
    body: {
      categoryId: input.categoryId,
      subject: input.subject,
      description: input.description,
      siteId: input.siteId ?? null,
      contactPhone: input.contactPhone,
      contactPhoneExtension: input.contactPhoneExtension ?? null,
      requesterUserId: input.requesterUserId ?? null,
    },
  });
}

/**
 * Puts somebody on a ticket that has just been created.
 *
 * ⚠️ The same endpoint the ticket's own participants panel uses, not a create-time
 * shortcut. Every guard runs there — who may manage the list, the site rule, category
 * clearance, and the high-severity audit event — so choosing people on the new-request form
 * is a convenience over the identical grant, never a second path to it.
 */
export function addParticipant(ticketNumber: string, userId: string): Promise<void> {
  return api(`/api/tickets/${encodeURIComponent(ticketNumber)}/participants`, {
    method: "POST",
    body: { userId },
  });
}

export type TicketSortField = "CreatedAt" | "UpdatedAt" | "Status" | "Number" | "Priority";

/**
 * The two "this needs looking at" narrowings the home screen counts and this queue applies.
 *
 * ⚠️ Neither is a status. Both describe a request that has quietly stopped moving while its
 * status still reads as though it were fine, which is why no other filter can express them.
 *
 * ⚠️ Compared member for member against the server's `AttentionFilter` by
 * `QueueFilterContractTests`, in both directions — the seam that produced the phantom
 * `Triage` status (D-60).
 */
export type TicketAttention = "AwaitingDeskReply" | "GoneQuiet" | "AwaitingRating" | "Unread";

export const attentionLabel: Record<TicketAttention, string> = {
  AwaitingDeskReply: "Waiting on a reply from us",
  GoneQuiet: "Gone quiet",
  AwaitingRating: "Waiting for your feedback",
  Unread: "Updates you haven't read",
};

export interface TicketListQuery {
  sortBy?: TicketSortField;
  direction?: "Ascending" | "Descending";
  pageSize?: number;
  cursor?: string;
  openOnly?: boolean;
  /**
   * ASSIGNED to the signed-in user — the agent queue's "My tickets". Resolved server-side
   * from the session, never sent as an id.
   *
   * ⚠️ Not the same question as `raisedByMe`. This one asks what you are working on; that
   * one asks what you have sent to the service desk. "My requests" sent this flag, so it
   * filtered a requester's own list on assignee, matched nothing, and every request anybody
   * raised was invisible to them from the moment they raised it.
   */
  mine?: boolean;

  /**
   * RAISED by the signed-in user — the "My requests" tab (D-47).
   *
   * ⚠️ A flag resolved from the session, like `mine` and `sharedWithMe` and unlike
   * `requesterUserId`: asking about yourself must never require the browser to hold its own
   * user id.
   */
  raisedByMe?: boolean;

  unassigned?: boolean;
  teamId?: string;
  siteId?: string;

  /**
   * Requests raised BY a particular person — the people screen's question.
   *
   * ⚠️ Unlike `mine`, this is a caller-supplied id, and the server treats it accordingly:
   * ignored unless the caller holds `user.manage` or it is their own. It can only narrow
   * within the query scope and RLS, so it never surfaces a ticket that was not already
   * readable.
   */
  requesterUserId?: string;

  /**
   * Requests assigned to a particular agent.
   *
   * ⚠️ Like `requesterUserId` and unlike `mine`, this is a caller-supplied id, and the
   * server treats it accordingly: ignored unless the caller holds `ticket.assign` — the
   * same permission that already returns the roster this picker is built from (D-31). It
   * narrows within the query scope and RLS, so filtering by an agent on a team you cannot
   * see returns an empty queue rather than a wider one.
   */
  assigneeUserId?: string;

  /**
   * Only requests raised by a VIP.
   *
   * ⚠️ Ungated, unlike `requesterUserId` and `assigneeUserId`, because it names nobody —
   * it describes the tickets the way `teamId` and `status` do. Applied on top of the query
   * filter and RLS like every other filter here, so it returns a subset of what the caller
   * could already list.
   */
  vipOnly?: boolean;

  /**
   * Requests somebody invited the signed-in user onto — the "Shared with me" tab (D-47).
   *
   * ⚠️ A flag, like `mine` and unlike `requesterUserId`, and for a stronger reason than
   * convenience: the server resolves it from the session, so there is no shape of this
   * request that asks which tickets a NAMED colleague was invited onto. That is a question
   * about somebody else's involvement, and no screen asks it.
   */
  sharedWithMe?: boolean;

  /**
   * Requests raised by the signed-in user's direct reports — "My team's requests" (D-47).
   *
   * ⚠️ Also a flag resolved from the session. There is no `managerUserId` parameter and
   * there must never be one.
   *
   * ⚠️ Returns only reports who have AGREED to share (D-46), and nothing at all when an
   * administrator has switched `reports.manager-access` off. Both are decided server-side;
   * the browser cannot tell an opted-out report from somebody with no reports, which is
   * the intended answer rather than a gap.
   */
  myReports?: boolean;

  status?: TicketStatus;
  minimumPriority?: TicketPriority;

  /**
   * When the request was RAISED — an ISO instant, inclusive.
   *
   * ⚠️ An instant rather than a date. A "date" filter sent as `2026-08-12` would mean a
   * different eight hours to the server than it did to the person who picked it, and every
   * request raised near midnight would land in the wrong day.
   *
   * ⚠️ The conversion happens in the browser, but NOT because this is the only layer that
   * knows the reader's zone — that was the reasoning here, and it was wrong. The server
   * resolves the authoritative answer (`TimeZones.ResolveAsync`) and sends it down; the
   * browser used its own instead, so a reader away from their site was silently given a
   * window around a different midnight. It converts here using the zone from the session —
   * see `startOfDayUtc` in `@/lib/time`, which requires one.
   */
  raisedFromUtc?: string;

  /**
   * The end of the range, EXCLUSIVE — midnight at the start of the day after the one picked.
   *
   * ⚠️ Exclusive on purpose. An inclusive bound has to be spelled as the last instant of a
   * day, and every version of that is wrong by some fraction of a second, which reads as a
   * filter that drops rows at random. See `endOfDayExclusiveUtc`.
   */
  raisedToUtc?: string;

  /**
   * "Waiting on a reply from us" or "gone quiet" — the two home-screen tiles, as a filter.
   *
   * ⚠️ The value is refused server-side rather than dropped if it is not one the API knows
   * (D-60): a dropped filter widens the queue past what was asked for and looks exactly like
   * a working screen.
   */
  attention?: TicketAttention;

  /**
   * Free text matched against the request's SUBJECT — the product's only free-text filter.
   *
   * ⚠️ The subject only, and never the description. Both are PHI-capable and neither
   * discloses a row the caller could not already open, but a substring search over the
   * NARRATIVE is the tool for "find every request that mentions this person", and a subject
   * search is not. Decided and enforced server-side; see `TicketFilter.SubjectContains`.
   *
   * ⚠️ Under three characters is REFUSED with a message, not dropped (D-60). Every row a
   * search returns files a PHI read against the caller's name, so a silently-ignored term
   * would hand back the whole readable queue under a heading saying these are the matches
   * and write a read event for each one.
   */
  search?: string;
}

// `startOfDayUtc` and `endOfDayExclusiveUtc` moved to `@/lib/time`, which resolves a day's
// bounds in a GIVEN zone rather than the browser's. They anchored to browser midnight, so a
// reader away from their site was handed a window of the right length around the wrong
// midnight — three hours of the neighbouring day, with nothing on screen saying so.

/**
 * Filtering happens on the server.
 *
 * Fetching everything and filtering in the browser is how the mock leaked internal notes
 * (defect D2). It also does not work: the list is paged, so a client-side filter only
 * ever searches the page it happens to have.
 */
export function listTickets(query: TicketListQuery = {}): Promise<TicketPage> {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }

  const qs = params.toString();
  return api<TicketPage>(`/api/tickets${qs ? `?${qs}` : ""}`);
}

/**
 * What a search by the readable number returns — enough to recognise the ticket and to
 * follow the link, and nothing else.
 */
export interface TicketLookup {
  /** The opaque number. This is what every route and API path takes. */
  number: string;
  displayNumber: number;
  subject: string;
  status: TicketStatus;
}

/**
 * Whatever somebody typed into a search box, understood as one identifier or neither.
 *
 * Accepts `412`, `#412`, ` 412 `, and the opaque `TF-7K2M-9QX4` in any case, because a
 * person pasting an identifier from an email has no idea which of the two they hold.
 *
 * ⚠️ Exact values only. There is no prefix or range form, and there must not be one: the
 * readable number is sequential, so "everything starting with 4" is an enumeration tool
 * rather than a search. The server enforces this independently — this function only
 * decides which of the two lookups to run.
 */
export function parseTicketQuery(
  input: string,
): { kind: "number"; value: string } | { kind: "displayNumber"; value: number } | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;

  // The opaque number, with or without its TF- prefix typed in lower case.
  if (/^TF-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/i.test(trimmed)) {
    return { kind: "number", value: trimmed.toUpperCase() };
  }

  // The readable number, with an optional # people copy along with it.
  const digits = /^#?(\d{1,9})$/.exec(trimmed);
  if (digits) {
    const value = Number(digits[1]);

    // The sequence starts at 1. Number() cannot overflow at nine digits, so a positive
    // result here is always a value the server can look up.
    if (value > 0) return { kind: "displayNumber", value };
  }

  return null;
}

/**
 * Resolves a readable number to the ticket it names (D-33).
 *
 * ⚠️ Returns null for BOTH "no such ticket" and "not yours to see" — the server sends 404
 * for each, deliberately, so the two cannot be told apart. Callers must not report which
 * one happened, because they do not know and neither does anyone else.
 */
export async function lookupTicketByDisplayNumber(
  displayNumber: number,
): Promise<TicketLookup | null> {
  try {
    return await api<TicketLookup>(`/api/tickets/resolve/${displayNumber}`);
  } catch {
    // Fails closed to "nothing found". A 404, a 429 and a network error all mean the same
    // thing to a search box: it cannot offer you a ticket.
    return null;
  }
}

/** Ordered most urgent first, for sorting and for choosing a colour. */
export const priorityRank: Record<TicketPriority, number> = {
  Critical: 5,
  Urgent: 4,
  High: 3,
  Normal: 2,
  Low: 1,
};

/** Status as a person would say it. The API sends PascalCase enum names. */
export const statusLabel: Record<TicketStatus, string> = {
  New: "New",
  InProgress: "In progress",
  AwaitingUser: "Awaiting you",
  OnHold: "On hold",
  Resolved: "Resolved",
  Closed: "Closed",
};

/**
 * How a status pill reads.
 *
 * Every entry carries a ring as well as a tint. A flat 15%-opacity fill has almost no
 * edge against a card in light mode and none at all against a dark one, so the pills read
 * as grey smudges — the ring is what makes them look deliberate in both themes without
 * raising the colour to the point where seven of them in a column start shouting.
 *
 * ⚠️ Colour is never the only signal. Every pill carries `statusLabel` as text, because a
 * colour-blind reader and a high-contrast theme both lose the hue.
 */
export const statusTone: Record<TicketStatus, string> = {
  New: "bg-primary/15 text-primary ring-1 ring-inset ring-primary/25",
  InProgress: "bg-accent text-accent-foreground ring-1 ring-inset ring-primary/30",
  AwaitingUser: "bg-warning/15 text-warning ring-1 ring-inset ring-warning/30",
  OnHold: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
  Resolved: "bg-success/15 text-success ring-1 ring-inset ring-success/30",
  Closed: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
};

/**
 * How the button that MOVES a ticket to a status reads.
 *
 * ⚠️ Distinct from {@link statusTone}, which describes the status a ticket is in. This
 * describes an action somebody is about to take, and the two want opposite things: a pill
 * has to sit quietly in a column of ninety, and a button has to be obviously pressable.
 *
 * The two forward moves — picking work up and finishing it — are filled, because they are
 * what an agent is here to do. Everything else is tinted: still coloured, still clearly a
 * button, but not competing with the action that matters. All of it is built from theme
 * tokens, so each one keeps its meaning and its contrast when the theme flips; a hard-coded
 * `bg-green-600` would be a different colour in dark mode and the same colour as
 * everything else in high contrast.
 */
/**
 * What the button that MOVES a ticket says on it.
 *
 * ⚠️ A VERB, and it takes the current status as well as the target. `statusLabel` names a
 * state a ticket is in — "Closed", "In progress" — and a button labelled with a noun tells
 * somebody what they will have done rather than what pressing it does. That was survivable
 * while only agents saw this panel, because an agent presses the same five buttons daily. It
 * is not survivable now that a requester sees it (D-61): their two buttons were "Closed" and
 * "In progress", and neither reads as "I'm done with this" or "this isn't fixed".
 *
 * ⚠️ `from` is needed because InProgress is two different actions. Arriving there from New,
 * Awaiting or On hold is the desk picking work up; arriving from Resolved or Closed is a
 * reopen. Same mapping as `TicketStatusRules.IsReopen` on the server — wording only, and
 * nothing here decides whether the move is allowed. The server sends the list.
 */
export function statusActionLabel(from: TicketStatus, to: TicketStatus): string {
  switch (to) {
    case "InProgress":
      return from === "Resolved" || from === "Closed" ? "Reopen" : "Start work";
    case "Resolved":
      return "Resolve";
    case "Closed":
      return "Close";
    case "AwaitingUser":
      return "Awaiting requester";
    case "OnHold":
      return "Put on hold";
    default:
      return statusLabel[to];
  }
}

export const statusActionTone: Record<TicketStatus, string> = {
  InProgress: "border-transparent bg-primary text-primary-foreground hover:opacity-90",
  Resolved: "border-transparent bg-success text-success-foreground hover:opacity-90",
  AwaitingUser: "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20",
  OnHold: "border-border bg-muted text-muted-foreground hover:bg-muted/70",
  Closed: "border-border bg-secondary text-secondary-foreground hover:bg-secondary/70",

  // Reopening is a correction rather than a step forward, so it is outlined rather than
  // filled — findable, and never the button somebody presses by muscle memory.
  New: "border-primary/40 bg-transparent text-primary hover:bg-primary/10",
};

/**
 * Relative age from a UTC timestamp.
 *
 * Computed against the real clock. The mock froze "now" at a fixed instant, so every
 * relative time was wrong the moment it was shown, in a way that looked plausible.
 */
export function ageOf(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;

  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

// ---------------------------------------------------------------------------
// Saved views
// ---------------------------------------------------------------------------

/**
 * A queue filter somebody saved for themselves.
 *
 * ⚠️ A saved view cannot widen what its owner sees. It is the same typed filter the
 * queue already accepts, applied on top of the server's query filter and row-level
 * security — there is no stored predicate, so there is nothing for it to widen with.
 *
 * Private to its owner. The API scopes every read and write to the session's user, and an
 * id belonging to someone else returns 404 rather than 403 — so it never confirms that
 * another person's view exists.
 */
export interface SavedView {
  id: string;
  name: string;
  openOnly: boolean;
  mine: boolean;
  unassigned: boolean;
  teamId: string | null;
  siteId: string | null;
  status: TicketStatus | null;
  minimumPriority: TicketPriority | null;
  sortBy: TicketSortField;
  direction: "Ascending" | "Descending";
  sortOrder: number;
}

/**
 * The parts of a saved view that behave like a queue TAB rather than like a filter.
 *
 * ⚠️ Deliberately not the whole view. Its team, site, status and priority are seeded into
 * the filter bar when the view is selected, so the bar always states what is actually
 * being applied and each chip can clear the thing it names. Returning them here as well
 * would produce a filter the bar shows as cleared and the query still applies — a control
 * that appears to do nothing, which is the exact failure the chips exist to prevent.
 */
export function savedViewToBaseQuery(view: SavedView): TicketListQuery {
  return {
    openOnly: view.openOnly || undefined,
    mine: view.mine || undefined,
    unassigned: view.unassigned || undefined,
  };
}

export function getSavedViews(): Promise<SavedView[]> {
  return api<SavedView[]>("/api/saved-views");
}

export function createSavedView(view: Omit<SavedView, "id">): Promise<SavedView> {
  return api<SavedView>("/api/saved-views", { method: "POST", body: view });
}

export function deleteSavedView(id: string): Promise<void> {
  return api<void>(`/api/saved-views/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Bulk actions
// ---------------------------------------------------------------------------

/**
 * What a bulk action did.
 *
 * ⚠️ Partial success is reported, never hidden. An agent who believes they triaged twenty
 * tickets, three of which were silently refused, has three sitting in a queue nobody is
 * watching — so the screen shows the refusals rather than a success message.
 *
 * A refusal reads the same whether the ticket is out of the caller's scope or does not
 * exist. That is deliberate on the server: distinguishing them would turn a bulk endpoint
 * into a way to discover which ticket numbers are real.
 */
export interface BulkResult {
  requested: number;
  succeeded: number;
  /**
   * ⚠️ `displayNumber` is null exactly when the ticket could not be resolved, which the
   * reason already says in words. It is not a second signal separating "does not exist"
   * from "not visible to you" — those two deliberately share one reason string.
   */
  failed: { number: string; displayNumber: number | null; reason: string }[];
}

export function bulkAssign(numbers: string[], assigneeUserId: string): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/assign", {
    method: "POST",
    body: { numbers, assigneeUserId },
  });
}

export function bulkPriority(numbers: string[], priority: TicketPriority): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/priority", {
    method: "POST",
    body: { numbers, priority },
  });
}

export function bulkStatus(numbers: string[], status: TicketStatus): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/status", {
    method: "POST",
    body: { numbers, status },
  });
}

// ---------------------------------------------------------------------------
// Routing to another team (D-96)
//
// ⚠️ Routing is a DISCLOSURE, not an edit: the destination team's agents gain a PHI-bearing
// request they have never seen, and the routing team usually loses it. Everything below is
// gated on `ticket.route` at the endpoint AND against the specific record, and the server
// refuses a destination that is not cleared for the request's classification — so a screen
// must never present these as ordinary field changes.
// ---------------------------------------------------------------------------

/** One destination category, and whether its team could actually read the request. */
export interface RouteTargetCategory {
  id: string;
  name: string;
  sensitivity: string;

  /**
   * ⚠️ False means the request would be invisible to everybody on the destination team, and the
   * server refuses it. The screen has to say so BEFORE somebody picks it, or the picker is full
   * of options that fail — the `AssignmentCandidates` argument applied to a team.
   */
  teamIsCleared: boolean;
}

export interface RouteTargetTeam {
  id: string;
  name: string;
  description: string;
  isCurrent: boolean;
  categories: RouteTargetCategory[];
}

export interface RouteTargets {
  currentTeamName: string;
  currentCategoryName: string;
  sensitivity: string;
  teams: RouteTargetTeam[];
}

/** The most requests one routing call may move (mirrors `TicketRoutingEndpoints.MaxRouteBatch`). */
export const MAX_ROUTE_BATCH = 10;

export const MAX_ROUTE_NOTE = 1000;

export function getRouteTargets(number: string): Promise<RouteTargets> {
  return api<RouteTargets>(`/api/tickets/${encodeURIComponent(number)}/route-targets`);
}

/**
 * Hands one request to another team.
 *
 * ⚠️ `note` goes on the THREAD and into no notification. An agent explaining why they cannot
 * take a request may reasonably write clinical detail into it, so it stays behind the request's
 * own authorization and never leaves the tenant.
 */
export function routeTicket(
  number: string,
  teamId: string,
  categoryId: string,
  note: string | null,
): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/route`, {
    method: "POST",
    body: { teamId, categoryId, note },
  });
}

/** The same operation over a selection — the server runs the identical code path per request. */
export function bulkRoute(
  numbers: string[],
  teamId: string,
  categoryId: string,
  note: string | null,
): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/route", {
    method: "POST",
    body: { numbers, teamId, categoryId, note },
  });
}

// ---------------------------------------------------------------------------
// Linked teams (D-97)
// ---------------------------------------------------------------------------

export interface TeamLink {
  id: string;
  partnerTeamId: string;
  partnerTeamName: string;
  reason: string;
  createdAtUtc: string;
  createdByName: string;
  revokedAtUtc: string | null;

  /**
   * What this team's agents will actually be able to open on the partner's queue.
   *
   * ⚠️ A link confers REACH and no CLEARANCE, so this can legitimately be empty on a link that
   * is live, correct and audited. Without it the screen would report a working arrangement over
   * an empty queue and nobody would know which of the two was wrong.
   */
  sharedClassifications: string[];

  /** The partner's classifications this team holds no clearance for — named, never counted. */
  unreachableClassifications: string[];
}

export interface TeamLinks {
  teamId: string;
  teamName: string;
  active: TeamLink[];
  ended: TeamLink[];
  candidates: { id: string; name: string; description: string }[];
}

export function getTeamLinks(teamId: string): Promise<TeamLinks> {
  return api<TeamLinks>(`/api/teams/${teamId}/links`);
}

/**
 * Links two teams so each may work the other's requests.
 *
 * ⚠️ The server requires `team.manage` over BOTH teams, and answers 403 with an explanation
 * rather than 404 when the caller administers only one — they picked the partner from a list, so
 * pretending it does not exist would be a lie that reads as a bug.
 */
export function createTeamLink(
  teamId: string,
  partnerTeamId: string,
  reason: string,
): Promise<{ id: string }> {
  return api<{ id: string }>(`/api/teams/${teamId}/links`, {
    method: "POST",
    body: { partnerTeamId, reason },
  });
}

/** Ends a link. One side is enough — ending can only ever narrow access. */
export function endTeamLink(teamId: string, linkId: string): Promise<void> {
  return api<void>(`/api/teams/${teamId}/links/${linkId}`, { method: "DELETE" });
}

export const TEAM_LINK_MAX_REASON = 400;

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export interface SlaPolicy {
  priority: TicketPriority;
  firstResponseMinutes: number;
  resolutionMinutes: number;
  clock: "BusinessHours" | "Continuous";
}

export interface BusinessHoursDay {
  day: string;
  isOpen: boolean;
  opensAtMinutes: number;
  closesAtMinutes: number;
}

export interface SlaSettings {
  policies: SlaPolicy[];
  hours: BusinessHoursDay[];
}

export function getSlaSettings(): Promise<SlaSettings> {
  return api<SlaSettings>("/api/settings/sla");
}

export function saveSlaPolicy(priority: string, body: Omit<SlaPolicy, "priority">): Promise<void> {
  return api(`/api/settings/sla/policies/${encodeURIComponent(priority)}`, {
    method: "PUT",
    body,
  });
}

export function saveBusinessHours(day: string, body: Omit<BusinessHoursDay, "day">): Promise<void> {
  return api(`/api/settings/sla/hours/${encodeURIComponent(day)}`, { method: "PUT", body });
}

// ---------------------------------------------------------------------------
// Team and category response targets
//
// The two tiers above the organisation's default, which stays in Settings. These are gated on
// sla.manage and additionally checked against the team, so a team administrator reaches the
// teams they administer and no others.
// ---------------------------------------------------------------------------

/** Which rung of the ladder a figure was resolved from. */
export type SlaTier = "Default" | "Team" | "Category";

export interface SlaTarget {
  priority: TicketPriority;
  firstResponseMinutes: number;
  resolutionMinutes: number;
  clock: "BusinessHours" | "Continuous";

  /**
   * ⚠️ Sent by the server, never inferred here. A screen that showed an inherited figure
   * without saying so would invite somebody to "correct" a number that is not set where they
   * are looking, and the edit would land on a row they did not mean.
   */
  tier: SlaTier;
}

export interface CategorySla {
  categoryId: string;
  name: string;
  sensitivity: string;
  isActive: boolean;
  targets: SlaTarget[];
}

export interface OperatingDay {
  day: string;
  isOpen: boolean;
  opensAtMinutes: number;
  closesAtMinutes: number;
}

export interface OperatingHours {
  /**
   * False when the team follows the organisation's week. The days are then the organisation's,
   * sent so an administrator can see what they would be changing from rather than starting a
   * schedule from a blank form.
   */
  isOwnSchedule: boolean;

  /**
   * ⚠️ Null while inheriting, and that is not a missing value. A team on the organisation's
   * week has each request measured at ITS OWN SITE, which no single identifier can express —
   * printing one would name a timezone the clock does not use.
   */
  timeZoneId: string | null;

  days: OperatingDay[];
}

export interface HolidayRule {
  id: string;
  name: string;
  kind: "FixedDate" | "NthWeekday";
  month: number;
  day: number | null;
  ordinal: number | null;
  weekday: string | null;
  observedOnNearestWeekday: boolean;

  /**
   * Days added to the resolved date, for a holiday defined relative to another.
   *
   * ⚠️ This is why "the day after Thanksgiving" is not stored as "the fourth Friday in
   * November". They coincide in 2026 and in 2024 the fourth Friday falls six days BEFORE
   * Thanksgiving, because that November has five Fridays.
   */
  offsetDays: number;

  isObserved: boolean;

  /**
   * ⚠️ Computed by the server, never here. "The fourth Thursday in November" is a rule somebody
   * can mistype into something that looks right and lands on the wrong day, and seeing the dates
   * is the only way to catch it — or to notice a rule that resolves to nothing at all, which
   * otherwise reads as a holiday that silently never closes anything.
   */
  nextDates: string[];
}

export interface HolidaySet {
  isOwnSet: boolean;
  holidays: HolidayRule[];
}

export interface TeamSla {
  teamId: string;
  teamName: string;
  hours: OperatingHours;
  holidays: HolidaySet;
  targets: SlaTarget[];
  categories: CategorySla[];
}

/** Starts a team's own list as a copy of the organisation's. */
export function adoptTeamHolidays(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/adopt`, { method: "POST" });
}

/**
 * The whole rule, on edit as well as create.
 *
 * ⚠️ It carries no `isObserved`. Whether the desk works a day is a different decision from when
 * that day falls — folding them together would mean fixing a typo in a rule silently switched
 * the holiday back on.
 */
export type HolidayRuleBody = Pick<
  HolidayRule,
  | "name"
  | "kind"
  | "month"
  | "day"
  | "ordinal"
  | "weekday"
  | "observedOnNearestWeekday"
  | "offsetDays"
>;

export function setTeamHolidayObserved(
  teamId: string,
  holidayId: string,
  isObserved: boolean,
): Promise<void> {
  return api(
    `/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}/observed`,
    { method: "PUT", body: { isObserved } },
  );
}

export function updateTeamHoliday(
  teamId: string,
  holidayId: string,
  body: HolidayRuleBody,
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}`, {
    method: "PUT",
    body,
  });
}

export function removeTeamHoliday(teamId: string, holidayId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}`, {
    method: "DELETE",
  });
}

/** Reverts the whole list to the organisation's. */
export function clearTeamHolidays(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays`, { method: "DELETE" });
}

export function addTeamHoliday(teamId: string, body: HolidayRuleBody): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays`, { method: "POST", body });
}

// The organisation's list, in Settings. Full CRUD — the desk's holiday schedule changes.
export function getOrganisationHolidays(): Promise<HolidayRule[]> {
  return api<HolidayRule[]>("/api/settings/sla/holidays");
}

export function addOrganisationHoliday(body: HolidayRuleBody): Promise<void> {
  return api("/api/settings/sla/holidays", { method: "POST", body });
}

export function updateOrganisationHoliday(holidayId: string, body: HolidayRuleBody): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}`, {
    method: "PUT",
    body,
  });
}

export function setOrganisationHolidayObserved(
  holidayId: string,
  isObserved: boolean,
): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}/observed`, {
    method: "PUT",
    body: { isObserved },
  });
}

/**
 * ⚠️ Reaches further than switching one off: a team taking its own list copies what the
 * organisation has AT THAT MOMENT, so a deleted rule is one no future team list starts from.
 * Teams already holding their own copy keep theirs.
 */
export function deleteOrganisationHoliday(holidayId: string): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}`, { method: "DELETE" });
}

/** ⚠️ All seven days, in one call — a team's hours are set as a week. */
export function saveTeamHours(
  teamId: string,
  body: { timeZoneId: string; days: OperatingDay[] },
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/hours`, { method: "PUT", body });
}

/** Returns the team to the organisation's week, measured at each request's own site. */
export function clearTeamHours(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/hours`, { method: "DELETE" });
}

export function getTeamSla(teamId: string): Promise<TeamSla> {
  return api<TeamSla>(`/api/teams/${encodeURIComponent(teamId)}/sla`);
}

export type SetSlaTargetBody = Omit<SlaTarget, "priority" | "tier">;

export function saveTeamSlaTarget(
  teamId: string,
  priority: string,
  body: SetSlaTargetBody,
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/sla/${encodeURIComponent(priority)}`, {
    method: "PUT",
    body,
  });
}

export function saveCategorySlaTarget(
  categoryId: string,
  priority: string,
  body: SetSlaTargetBody,
): Promise<void> {
  return api(
    `/api/categories/${encodeURIComponent(categoryId)}/sla/${encodeURIComponent(priority)}`,
    { method: "PUT", body },
  );
}

/** Removes an OVERRIDE, never a commitment — what is left is whatever the next rung says. */
export function clearTeamSlaTarget(teamId: string, priority: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/sla/${encodeURIComponent(priority)}`, {
    method: "DELETE",
  });
}

export function clearCategorySlaTarget(categoryId: string, priority: string): Promise<void> {
  return api(
    `/api/categories/${encodeURIComponent(categoryId)}/sla/${encodeURIComponent(priority)}`,
    { method: "DELETE" },
  );
}

/**
 * Minutes as something a person reads.
 *
 * Targets are stored in minutes because "one business day" is only a duration once opening
 * hours are known — but 3600 is not a number anyone can judge, so the screen says
 * "5 business days" and the field underneath still holds the minutes.
 */
export function describeMinutes(minutes: number, businessDayMinutes = 720): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < businessDayMinutes) {
    const hours = minutes / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
  }

  const days = minutes / businessDayMinutes;
  return `${Number.isInteger(days) ? days : days.toFixed(1)} business ${days === 1 ? "day" : "days"}`;
}

/** "07:00" from 420. The API speaks minutes; an input speaks HH:mm. */
export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function timeToMinutes(value: string): number {
  const [h, m] = value.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Somebody a ticket can be given to. A name and an id — never a directory row. */
export interface AssignableUser {
  id: string;
  displayName: string;

  /**
   * ⚠️ Optional because two endpoints produce this shape and only one carries the detail.
   *
   * `/api/tickets/{n}/assignable` is the picker — one ticket, people who may actually take
   * it — and sends email, job title and avatar state so the person assigning a clinical
   * request can tell two D. Nguyens apart (D-38). `/api/reference/assignees` is the queue's
   * FILTER roster, a much wider list, and deliberately still sends a name and an id.
   */
  email?: string | null;
  jobTitle?: string | null;
  hasAvatar?: boolean;
}

/**
 * Everyone who can be given work, for the queue's assignee filter.
 *
 * ⚠️ Distinct from {@link getAssignable}, which is scoped to ONE ticket and is the only
 * thing an assignment may be made from. This is the roster, and it is gated on
 * `ticket.assign` server-side — call it only when the session holds that permission, or it
 * 403s and the filter renders empty for no visible reason.
 */
export function getAssigneeRoster(): Promise<AssignableUser[]> {
  return api<{ id: string; name: string }[]>("/api/reference/assignees").then((rows) =>
    rows.map((r) => ({ id: r.id, displayName: r.name })),
  );
}

export function getAssignable(number: string): Promise<AssignableUser[]> {
  return api<AssignableUser[]>(`/api/tickets/${encodeURIComponent(number)}/assignable`);
}

export function assignTicket(number: string, assigneeUserId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/assign`, {
    method: "POST",
    body: { assigneeUserId },
  });
}

/**
 * People who can take EVERY ticket in a selection.
 *
 * The intersection, computed server-side. Offering somebody who can take four of six
 * produces a half-failed bulk action the picker should not have invited.
 *
 * POST because the selection travels in the body — forty ticket numbers in a query string
 * is a URL nobody can log or debug, and it would sit in browser history.
 */
export function bulkAssignable(numbers: string[]): Promise<AssignableUser[]> {
  return api<AssignableUser[]>("/api/tickets/bulk/assignable", {
    method: "POST",
    body: { numbers },
  });
}
